import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import Icon from '../../components/Icon';
import { FiFileText, FiVideo, FiRadio, FiRefreshCw, FiTrendingUp } from 'react-icons/fi';
import { fetchCompanyIdAndPsoInfo, fetchManagedPsos, fetchRequests } from '../request_management/request_form/service/requestManagementService';
import { fetchChangeOfVendorsData } from '../request_management/change_of_vendor/service/changeOfVendorService';

type DashboardStat = {
    title: string;
    value: number;
    icon: any;
    color: string;
    route: string;
    loading: boolean;
};

export default function RegionalHeadDashboard() {
    const pso = useAppSelector((s) => s.user.pso);
    const navigate = useNavigate();
    const [stats, setStats] = useState<DashboardStat[]>([
        {
            title: 'Request Form',
            value: 0,
            icon: FiFileText,
            color: 'bg-blue-500',
            route: '/request-form',
            loading: true
        },
        {
            title: 'CCTV',
            value: 0,
            icon: FiVideo,
            color: 'bg-purple-500',
            route: '/cctv',
            loading: true
        },
        {
            title: 'BAMS',
            value: 0,
            icon: FiRadio,
            color: 'bg-green-500',
            route: '/bams',
            loading: true
        },
        {
            title: 'Change of Vendor',
            value: 0,
            icon: FiRefreshCw,
            color: 'bg-orange-500',
            route: '/change-of-vendor',
            loading: true
        }
    ]);

    const [companyId, setCompanyId] = useState<string | null>(null);
    const [psoId, setPsoId] = useState<string | null>(null);
    const [psoDesignation, setPsoDesignation] = useState<string | null>(null);
    const [managedPsoIds, setManagedPsoIds] = useState<string[]>([]);
    const uid = auth.currentUser?.uid;

    // Fetch company and PSO info
    useEffect(() => {
        if (!uid) return;

        const loadCompanyInfo = async () => {
            try {
                const companyInfo = await fetchCompanyIdAndPsoInfo(uid);
                if (companyInfo) {
                    setCompanyId(companyInfo.companyId);
                    setPsoId(companyInfo.psoId);
                    setPsoDesignation(companyInfo.psoDesignation);
                }
            } catch (err) {
                console.error('Error fetching company info:', err);
            }
        };

        loadCompanyInfo();
    }, [uid]);

    // Fetch managed PSOs
    useEffect(() => {
        if (!companyId || !psoId || !psoDesignation || !uid) return;

        const loadManagedPsos = async () => {
            try {
                const managedIds = await fetchManagedPsos(companyId, psoId, psoDesignation, uid);
                setManagedPsoIds(managedIds);
            } catch (error) {
                console.error('Error fetching managed PSOs:', error);
                setManagedPsoIds([]);
            }
        };

        loadManagedPsos();
    }, [companyId, psoId, psoDesignation, uid]);

    // Fetch pending counts
    useEffect(() => {
        if (!companyId || !psoId || !psoDesignation) return;

        const fetchPendingCounts = async () => {
            try {
                // 1. Fetch Request Form pending count
                let requestFormCount = 0;
                try {
                    const requestsList = await fetchRequests(companyId, psoId, psoDesignation, managedPsoIds);
                    requestFormCount = requestsList.filter(req => req.status === 'pending').length;
                } catch (error) {
                    console.error('Error fetching request forms:', error);
                }

                // 2. Fetch CCTV and BAMS pending counts (fetch once, filter in memory)
                let cctvCount = 0;
                let bamsCount = 0;
                try {
                    const hardwareSnap = await getDocs(
                        query(
                            collection(db, 'banks', companyId, 'hardware_requests'),
                            orderBy('createdAt', 'desc')
                        )
                    );
                    const allHardwareRequests = hardwareSnap.docs.map(doc => ({ 
                        id: doc.id, 
                        ...doc.data() 
                    }));
                    
                    // Filter CCTV and BAMS in one pass
                    const pendingCctv = allHardwareRequests.filter((req: any) => 
                        req.requestType === 'CCTV' && 
                        req.status === 'pending' && 
                        managedPsoIds.includes(req.createdBy || '')
                    );
                    cctvCount = pendingCctv.length;
                    
                    const pendingBams = allHardwareRequests.filter((req: any) => 
                        req.requestType === 'BAMS' && 
                        req.status === 'pending' && 
                        managedPsoIds.includes(req.createdBy || '')
                    );
                    bamsCount = pendingBams.length;
                } catch (error) {
                    console.error('Error fetching hardware requests:', error);
                }

                // 4. Fetch Change of Vendor pending count
                let changeOfVendorCount = 0;
                try {
                    const changeOfVendors = await fetchChangeOfVendorsData(uid || '', false, true, psoDesignation);
                    // Filter by pending status (case-insensitive) and managed PSOs
                    changeOfVendorCount = changeOfVendors.filter(req => {
                        const status = req.status?.toLowerCase() || '';
                        return status === 'pending' && managedPsoIds.includes(req.createdBy || '');
                    }).length;
                } catch (error) {
                    console.error('Error fetching change of vendor requests:', error);
                }

                // Update stats
                setStats([
                    {
                        title: 'Request Form',
                        value: requestFormCount,
                        icon: FiFileText,
                        color: 'bg-blue-500',
                        route: '/request-form',
                        loading: false
                    },
                    {
                        title: 'CCTV',
                        value: cctvCount,
                        icon: FiVideo,
                        color: 'bg-purple-500',
                        route: '/cctv',
                        loading: false
                    },
                    {
                        title: 'BAMS',
                        value: bamsCount,
                        icon: FiRadio,
                        color: 'bg-green-500',
                        route: '/bams',
                        loading: false
                    },
                    {
                        title: 'Change of Vendor',
                        value: changeOfVendorCount,
                        icon: FiRefreshCw,
                        color: 'bg-orange-500',
                        route: '/change-of-vendor',
                        loading: false
                    }
                ]);
            } catch (error) {
                console.error('Error fetching pending counts:', error);
            }
        };

        fetchPendingCounts();
    }, [companyId, psoId, psoDesignation, managedPsoIds, uid]);

    const handleStatClick = useCallback((route: string) => {
        navigate(route);
    }, [navigate]);

    const totalPending = useMemo(() => stats.reduce((sum, stat) => sum + stat.value, 0), [stats]);

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Regional Head Dashboard
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Welcome back, {pso?.name || 'User'}. Overview of pending requests.
                </p>
            </div>

            {/* Total Pending Card */}
            <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600 p-6 shadow-lg dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-blue-100">Total Pending Requests</p>
                        <p className="mt-2 text-4xl font-bold text-white">{totalPending}</p>
                        <p className="mt-1 text-xs text-blue-100">Across all request types</p>
                    </div>
                    <div className="rounded-full bg-white/20 p-4">
                        <Icon icon={FiTrendingUp} className="h-8 w-8 text-white" />
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {stats.map((stat, index) => (
                    <div
                        key={index}
                        onClick={() => handleStatClick(stat.route)}
                        className="group cursor-pointer rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {stat.title}
                                </p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                                    {stat.loading ? (
                                        <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-gray-400 border-r-transparent"></span>
                                    ) : (
                                        stat.value
                                    )}
                                </p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Pending requests
                                </p>
                            </div>
                            <div className={`rounded-full ${stat.color} p-3 transition-transform group-hover:scale-110`}>
                                <Icon icon={stat.icon} className="h-6 w-6 text-white" />
                            </div>
                        </div>
                        {/* Progress bar */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                                className={`h-full ${stat.color} transition-all`}
                                style={{
                                    width: totalPending > 0 ? `${(stat.value / totalPending) * 100}%` : '0%'
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Chart Visualization */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Pending Requests Overview
                </h3>
                <div className="space-y-4">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            onClick={() => handleStatClick(stat.route)}
                            className="group cursor-pointer rounded-lg border border-gray-200 p-4 transition-all hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`rounded-lg ${stat.color} p-2`}>
                                        <Icon icon={stat.icon} className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-gray-100">
                                            {stat.title}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {stat.loading ? 'Loading...' : `${stat.value} pending requests`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                            {stat.loading ? '-' : stat.value}
                                        </p>
                                        {totalPending > 0 && !stat.loading && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {((stat.value / totalPending) * 100).toFixed(1)}%
                                            </p>
                                        )}
                                    </div>
                                    {/* Bar chart */}
                                    <div className="h-12 w-32 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                        <div
                                            className={`h-full ${stat.color} transition-all`}
                                            style={{
                                                width: totalPending > 0 ? `${(stat.value / totalPending) * 100}%` : '0%'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

